import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faTrash,
  faSave,
  faCheckCircle,
  faQuestionCircle,
  faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';

const LessonStudio = () => {
  const { id } = useParams();
  const [lesson, setLesson] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [activeContent, setActiveContent] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content'); // content | downloads | quiz

  // Download modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadForm, setDownloadForm] = useState({
    name: '',
    description: '',
    file_path: '',
    mime_type: 'application/pdf',
  });

  const loadLessonData = async () => {
    try {
      setLoading(true);
      const data = await streetlearnApi.getLessonById(id);
      setLesson(data);
      setVersions(data.versions || []);
      setDownloads(data.downloads || []);
      setQuestions(data.questions || []);

      if (data.current_content) {
        setSelectedVersionId(data.current_content.id);
        setActiveContent(data.current_content);
      } else if (data.versions && data.versions.length > 0) {
        setSelectedVersionId(data.versions[0].id);
        setActiveContent(data.versions[0]);
      }
    } catch (err) {
      console.error('Failed to load lesson:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLessonData();
  }, [id]);

  const handleSelectVersion = async (contentId) => {
    setSelectedVersionId(contentId);
    try {
      const content = await streetlearnApi.getLessonContent(contentId);
      setActiveContent(content);
    } catch (err) {
      console.error('Failed to load content version:', err);
    }
  };

  const handleCreateNewVersion = async () => {
    if (!window.confirm('Create a new draft version based on the current version?')) return;
    try {
      setSaving(true);
      const newVersion = await streetlearnApi.createLessonVersion(id, {
        copy_from_version_id: selectedVersionId,
        change_note: `Draft version started from v${activeContent?.version || 1}`,
      });
      showToast.success(`New version ${newVersion.version} created!`);
      loadLessonData();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to create new version.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContent = async () => {
    if (!activeContent) return;
    try {
      setSaving(true);
      await streetlearnApi.updateLessonContent(activeContent.id, {
        objective: activeContent.objective,
        sections: activeContent.sections,
        example: activeContent.example,
        faq: activeContent.faq,
        glance: activeContent.glance,
        video: activeContent.video,
        change_note: activeContent.change_note,
      });
      showToast.success('Content version updated successfully!');
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to update content.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    if (!activeContent) return;
    try {
      setSaving(true);
      await streetlearnApi.submitVersionForReview(activeContent.id);
      showToast.success(`Version ${activeContent.version} submitted for review!`);
      loadLessonData();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to submit version.');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveVersion = async () => {
    if (!activeContent) return;
    try {
      setSaving(true);
      await streetlearnApi.approveLessonVersion(activeContent.id);
      showToast.success(`Version ${activeContent.version} approved! Ready to publish.`);
      loadLessonData();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to approve version.');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishVersion = async () => {
    if (!activeContent) return;
    if (
      !window.confirm(
        `Are you sure you want to PUBLISH version ${activeContent.version}? This will make it live to learners and archive previous versions.`
      )
    )
      return;

    try {
      setSaving(true);
      await streetlearnApi.publishLessonVersion(activeContent.id);
      showToast.success(`Version ${activeContent.version} is now live and published!`);
      loadLessonData();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to publish version.');
    } finally {
      setSaving(false);
    }
  };

  // Downloads handlers
  const handleAddDownload = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await streetlearnApi.createDownload({
        lesson_id: Number(id),
        ...downloadForm,
      });
      showToast.success('Download resource added successfully!');
      setShowDownloadModal(false);
      setDownloadForm({ name: '', description: '', file_path: '', mime_type: 'application/pdf' });
      loadLessonData();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to add download.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDownload = async (downloadId) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;
    try {
      await streetlearnApi.deleteDownload(downloadId);
      showToast.success('Download resource deleted successfully!');
      loadLessonData();
    } catch (err) {
      showToast.error(err?.response?.data?.message || 'Failed to delete download.');
    }
  };

  if (loading) {
    return (
      <div className="mt-5 text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="text-muted mt-2">Opening Lesson Studio...</p>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="mt-5 text-center py-5">
        <h4>Lesson not found.</h4>
        <Link to="/streetlearn/curriculum" className="btn btn-default btn-sm mt-2">
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Curriculum
        </Link>
      </div>
    );
  }

  const isEditable = activeContent?.status === 'draft' || activeContent?.status === 'in_review';

  return (
    <div className="mt-5">
      {/* Breadcrumb / Top Bar */}
      <div className="mb-2">
        <Link to="/streetlearn/curriculum" className="btn btn-default btn-sm">
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Curriculum
        </Link>
      </div>

      {/* Lesson Studio Header Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body p-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  background: '#f1f5f9',
                  color: '#334155',
                  padding: '2px 8px',
                  borderRadius: '3px',
                  border: '1px solid #cbd5e1',
                }}
              >
                {lesson.code}
              </span>
              <span className="text-muted small font-weight-bold">
                Level {lesson.level?.number}: {lesson.level?.title}
              </span>
              <span className="text-muted small">&bull;</span>
              <span className="text-muted small">
                {lesson.module?.title}
              </span>
            </div>
            <h4 className="font-weight-bold text-dark m-0 mb-1" style={{ fontSize: '18px' }}>
              {lesson.title}
            </h4>
            <div className="text-muted small d-flex align-items-center flex-wrap gap-2">
              <span>Duration: <strong>{lesson.duration_min || 15} mins</strong></span>
              <span>&bull;</span>
              <span>Type: <strong>{lesson.type_code}</strong></span>
              <span>&bull;</span>
              {lesson.is_free ? (
                <span className="text-success font-weight-bold">Free Preview</span>
              ) : (
                <span className="text-muted">Subscribers Only</span>
              )}
            </div>
          </div>

          {/* Version Switcher & Workflow Actions */}
          <div className="d-flex flex-column align-items-end gap-2 ms-auto">
            <div className="d-flex align-items-center gap-2">
              <label className="text-muted small mb-0 font-weight-bold text-nowrap">Version:</label>
              <select
                className="form-control input-sm"
                style={{ width: '160px', height: '30px', padding: '3px 8px', fontSize: '12px' }}
                value={selectedVersionId || ''}
                onChange={(e) => handleSelectVersion(Number(e.target.value))}
              >
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version} — {v.status.toUpperCase()}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-default btn-sm text-nowrap"
                style={{ height: '30px', padding: '4px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
                onClick={handleCreateNewVersion}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> New Version
              </button>
            </div>

            {/* Workflow Buttons */}
            <div className="d-flex align-items-center justify-content-end gap-2">
              {activeContent?.status === 'draft' && (
                <button
                  className="btn btn-warning btn-sm text-nowrap"
                  style={{ height: '30px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}
                  onClick={handleSubmitForReview}
                  disabled={saving}
                >
                  Submit for Review
                </button>
              )}
              {activeContent?.status === 'in_review' && (
                <button
                  className="btn btn-info btn-sm text-nowrap"
                  style={{ height: '30px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}
                  onClick={handleApproveVersion}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Approve Version
                </button>
              )}
              {(activeContent?.status === 'approved' || activeContent?.status === 'draft') && (
                <button
                  className="btn btn-success btn-sm text-nowrap"
                  style={{ height: '30px', padding: '4px 12px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}
                  onClick={handlePublishVersion}
                  disabled={saving}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Publish Live
                </button>
              )}
              {activeContent?.status === 'published' && (
                <span
                  className="text-success font-weight-bold d-inline-flex align-items-center text-nowrap"
                  style={{
                    height: '30px',
                    background: '#eafaf1',
                    border: '1px solid #c3e6cb',
                    borderRadius: '4px',
                    padding: '4px 12px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Active Live Version
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Studio Tab Buttons */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body py-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'content' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('content')}
            >
              Lesson Content & Sections (v{activeContent?.version || 1})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'downloads' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('downloads')}
            >
              Cheatsheets & Downloads ({downloads.length})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'quiz' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('quiz')}
            >
              Lesson Quiz Questions ({questions.length})
            </button>
          </div>
        </div>
      </div>

      {/* Tab 1: Content Editor */}
      {activeTab === 'content' && activeContent && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <h4 className="panel-title m-0">
              Content Editor — Version {activeContent.version} ({activeContent.status})
            </h4>
            {isEditable && (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSaveContent}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faSave} className="me-1" /> {saving ? 'Saving...' : 'Save Draft'}
              </button>
            )}
          </div>
          <div className="panel-body">
            {!isEditable && (
              <div className="alert alert-info py-2 mb-3">
                This version is <strong>{activeContent.status}</strong> and cannot be directly edited.
                Click <strong>"+ New Version"</strong> above to create an editable draft.
              </div>
            )}

            {/* Learning Objective */}
            <div className="form-group mb-3">
              <label className="font-semibold text-sm">
                Learning Objective (Key takeaway)
              </label>
              <textarea
                className="form-control"
                rows="2"
                disabled={!isEditable}
                value={activeContent.objective || ''}
                onChange={(e) =>
                  setActiveContent({ ...activeContent, objective: e.target.value })
                }
                placeholder="What will the student be able to execute or analyze after completing this lesson?"
              />
            </div>

            {/* Video Lesson URL */}
            <div className="form-group mb-3">
              <label className="font-semibold text-sm">Video Stream / Embed URL</label>
              <input
                type="text"
                className="form-control"
                disabled={!isEditable}
                value={activeContent.video?.url || ''}
                onChange={(e) =>
                  setActiveContent({
                    ...activeContent,
                    video: { ...activeContent.video, url: e.target.value },
                  })
                }
                placeholder="https://vimeo.com/... or S3 video URL"
              />
            </div>

            {/* Sections Markdown/Text */}
            <div className="form-group mb-3">
              <label className="font-semibold text-sm">
                Lesson Body / Concept Explanations
              </label>
              <textarea
                className="form-control font-mono"
                rows="10"
                disabled={!isEditable}
                value={
                  typeof activeContent.sections === 'string'
                    ? activeContent.sections
                    : JSON.stringify(activeContent.sections || '', null, 2)
                }
                onChange={(e) =>
                  setActiveContent({ ...activeContent, sections: e.target.value })
                }
                placeholder="Write the primary lesson content in Markdown or JSON blocks..."
              />
            </div>

            {/* Practical Example */}
            <div className="form-group mb-3">
              <label className="font-semibold text-sm">
                Practical Market Example / Case Demonstration
              </label>
              <textarea
                className="form-control"
                rows="4"
                disabled={!isEditable}
                value={
                  typeof activeContent.example === 'object' && activeContent.example !== null
                    ? (activeContent.example.html ? activeContent.example.html.replace(/<[^>]*>/g, '') : JSON.stringify(activeContent.example, null, 2))
                    : (activeContent.example || '')
                }
                onChange={(e) =>
                  setActiveContent({ ...activeContent, example: e.target.value })
                }
                placeholder="Real stock/chart scenario demonstrating the concept..."
              />
            </div>

            {/* Change Note */}
            <div className="form-group mb-3">
              <label className="font-semibold text-sm">Change Log Note</label>
              <input
                type="text"
                className="form-control"
                disabled={!isEditable}
                value={activeContent.change_note || ''}
                onChange={(e) =>
                  setActiveContent({ ...activeContent, change_note: e.target.value })
                }
                placeholder="e.g. Updated chart screenshots for 2026 data"
              />
            </div>
          </div>
          {isEditable && (
            <div className="panel-footer bg-white border-top text-right">
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSaveContent}
                disabled={saving}
              >
                <FontAwesomeIcon icon={faSave} className="me-1" /> {saving ? 'Saving...' : 'Save Draft'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Downloads & Cheatsheets */}
      {activeTab === 'downloads' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <h4 className="panel-title m-0">Lesson Cheatsheets & Downloads</h4>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowDownloadModal(true)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" /> Add Resource
            </button>
          </div>
          <div className="panel-body p-0">
            {downloads.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No downloadable resources attached to this lesson yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-condensed table-hover bg-white m-0">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Description</th>
                      <th>File / Link</th>
                      <th style={{ minWidth: '100px' }}>Format</th>
                      <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {downloads.map((d) => (
                      <tr key={d.id}>
                        <td>
                          <strong>
                            <FontAwesomeIcon icon={faFilePdf} className="me-1 text-danger" />
                            {d.name}
                          </strong>
                        </td>
                        <td className="text-muted small">{d.description || '-'}</td>
                        <td>
                          <a
                            href={d.file_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary small"
                          >
                            {d.file_path}
                          </a>
                        </td>
                        <td>
                          <span className="text-muted small">{d.mime_type}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn btn-danger btn-xs"
                            onClick={() => handleDeleteDownload(d.id)}
                            title="Delete Resource"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab 3: Quiz Questions */}
      {activeTab === 'quiz' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0 d-flex justify-content-between align-items-center">
            <h4 className="panel-title m-0">Lesson End-of-Chapter Quiz Pool</h4>
            <Link to="/streetlearn/questions" className="btn btn-default btn-sm">
              <FontAwesomeIcon icon={faQuestionCircle} className="me-1" /> Open Full Question Bank
            </Link>
          </div>
          <div className="panel-body p-0">
            {questions.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No quiz questions assigned to this lesson yet.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-condensed table-hover bg-white m-0">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>ID</th>
                      <th>Question Stem &amp; Options</th>
                      <th style={{ minWidth: '90px' }}>Usage</th>
                      <th style={{ minWidth: '90px' }}>Status</th>
                      <th style={{ minWidth: '100px' }}>Serves</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q) => {
                      const opts = Array.isArray(q.options)
                        ? q.options
                        : typeof q.options === 'string'
                        ? JSON.parse(q.options || '[]')
                        : [];
                      return (
                        <tr key={q.id}>
                          <td className="font-bold text-muted">#{q.id}</td>
                          <td>
                            <div className="font-bold text-dark">{q.stem}</div>
                            <div className="small text-muted mt-1">
                              {opts.map((opt, i) => (
                                <span
                                  key={i}
                                  className={`me-3 ${
                                    i === q.correct_index ? 'text-success font-bold' : ''
                                  }`}
                                >
                                  {String.fromCharCode(65 + i)}) {opt}
                                  {i === q.correct_index && ' ✓'}
                                </span>
                              ))}
                            </div>
                          </td>
                          <td>
                            <span className="text-info font-weight-bold">{q.use_in}</span>
                          </td>
                          <td>
                            {q.status === 'published' ? (
                              <span className="text-success font-weight-bold">Published</span>
                            ) : (
                              <span className="text-warning font-weight-bold">Draft</span>
                            )}
                          </td>
                          <td className="small text-muted">{q.times_served || 0} times</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleAddDownload}>
                <div className="modal-header d-flex justify-content-between align-items-center">
                  <h4 className="modal-title font-bold m-0">Add Cheatsheet / Download Resource</h4>
                  <button
                    type="button"
                    className="close"
                    style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                    onClick={() => setShowDownloadModal(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      Resource Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={downloadForm.name}
                      onChange={(e) => setDownloadForm({ ...downloadForm, name: e.target.value })}
                      placeholder="e.g. Candlestick Quick Reference PDF"
                      required
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">Description</label>
                    <input
                      type="text"
                      className="form-control"
                      value={downloadForm.description}
                      onChange={(e) =>
                        setDownloadForm({ ...downloadForm, description: e.target.value })
                      }
                      placeholder="Brief note on when to refer to this cheatsheet..."
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="font-semibold text-sm">
                      File S3 Path or URL <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={downloadForm.file_path}
                      onChange={(e) =>
                        setDownloadForm({ ...downloadForm, file_path: e.target.value })
                      }
                      placeholder="https://sgapp-data.s3.amazonaws.com/... or file path"
                      required
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => setShowDownloadModal(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                    {saving ? 'Adding...' : 'Add Resource'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonStudio;
